import { Circle } from '@factorialco/f0-react/icons/app'
import { Files, Sleep, Tablet } from '@factorialco/f0-react/icons/app'
import { F0Select } from '@factorialco/f0-react'
import { FormatBar } from 'modules/shift_management/components/ShiftManagement/FormatBar'
import { useShiftTableFormatBar } from 'modules/shift_management/hooks/formatBar/useShiftTableFormatBar'
import DeleteButton from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/ShiftActionsBar/DeleteButton'
import type { View } from 'modules/shift_management/lib/types'
import type { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type DefaultShift from 'modules/shift_management/store/internal/models/DefaultShift'
import i18n from 'modules/core/lib/i18n'

interface ShiftTableFormatBarProps {
  gridRoot: HTMLDivElement | null
  hasMore: boolean
  isDefaultShiftsLoading: boolean
  onCopy: () => void
  onPaste: () => void
  openDeleteFlow: () => void
  selection: GridSelection
  selectionState: ReturnType<typeof useSelectionState>
  view: View
  visibleDefaultShifts: {
    active: DefaultShift[]
  }
  clipboardActionsEnabled?: boolean
}

const ShiftTableFormatBar = ({
  gridRoot,
  hasMore,
  isDefaultShiftsLoading,
  onCopy,
  onPaste,
  openDeleteFlow,
  selection,
  selectionState,
  view,
  visibleDefaultShifts,
  clipboardActionsEnabled = true,
}: ShiftTableFormatBarProps) => {
  const {
    options,
    selectedValues,
    handleSelect,
    onAddRestDays,
    isAddingRestDays,
    isAddRestDaysDisabled,
    isCopyDisabled,
    isPasteDisabled,
    onPaste: onPasteFromHook,
  } = useShiftTableFormatBar({
    daysByRow: selectionState.daysByRow,
    hasMore,
    onCopy,
    onPaste,
    selectionKind: selection.kind,
    selectedShifts: selectionState.shifts,
    view,
    visibleDefaultShifts,
  })

  if (!clipboardActionsEnabled) return null

  const selectedDefaultShiftsKey = selectedValues.slice().sort().join(',')

  return (
    <FormatBar
      visible={
        selectionState.isEnabled &&
        selection.kind !== 'none' &&
        selection.kind !== 'all' &&
        selection.kind !== 'event'
      }
      selection={selection}
      gridRoot={gridRoot}
      actions={[
        {
          icon: Sleep,
          label: i18n.t('shift_management.actions.add_rest_day'),
          onClick: onAddRestDays,
          disabled: isAddingRestDays || isAddRestDaysDisabled,
        },
        {
          icon: Files,
          label: i18n.t('shift_management.actions.copy_to_clipboard'),
          onClick: onCopy,
          disabled: isCopyDisabled,
        },
        {
          icon: Tablet,
          label: i18n.t('common.paste'),
          onClick: onPasteFromHook,
          disabled: isPasteDisabled,
        },
      ]}
      secondaryButton={<DeleteButton variant="critical" onDeleteAction={openDeleteFlow} />}
    >
      <F0Select
        key={selectedDefaultShiftsKey}
        options={options}
        value={selectedValues}
        onChange={(value: string[] | string | null) => {
          void handleSelect(value)
        }}
        withApplySelection
        size="sm"
        loading={isDefaultShiftsLoading}
        icon={Circle}
        label={i18n.t('shift_management.actions.assign_shift')}
        placeholder={i18n.t('shift_management.actions.assign_shift')}
        hideLabel
        showSearchBox
        multiple
      />
    </FormatBar>
  )
}

export { ShiftTableFormatBar }
